import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import accountsImageSrc from '../../../../../../../../src/assets/images/accounts.svg';
import {VirtualTableSortableColumnDirectionsEnum} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {useAsyncTableSource} from '../../../../../../../../src/components/common/table/helpers/useAsyncTableSource';
import {
  SelectedColumnSort,
  TableColumn,
} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {
  VirtualTable,
  VirtualTableHandle,
} from '../../../../../../../../src/components/common/table/virtualTable';
import {accountsLearnMoreLink} from '../../../../../../../../src/helpers/accountHelpers';
import {makeContactsManagerAccountDetailsUrl} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {TableTokenPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerTableStatusesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {
  accountsTableColumns,
  AccountsTableColumnsEnum,
  accountsTableSortableColumns,
  mapAccountIdToSortableColumnName,
} from '../common/accounts/accountsTableConfig';
import {contactsManagerAccountsSortColumns} from '../common/accounts/accountsTableHelpers';
import {AccountsTableRowContainer} from '../common/accounts/accountsTableRowContainer';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {ContactsManagerTableEmptyState} from '../common/contactsManagerEmptyState';

/*
 * Constants.
 */

const initialColumnSort: SelectedColumnSort = {
  id: accountsTableSortableColumns[0].id,
  direction: accountsTableSortableColumns[0].directions[0],
};

/*
 * Props.
 */

interface AccountsTableProps {
  panelId: ContactsManagerPanelId;
  dynamicColumns: TableColumn<
    AccountsTableColumnsEnum,
    ContactsManagerCustomFieldColumnMetadata | undefined
  >[];
  tableStatus?: ContactsManagerTableStatusesEnum;
  selectedAccountId: number | undefined;
  accountIds: ReadonlyArray<number>;
  paginationData?: TableTokenPaginationDataModel;
  onReset: Handler;
  navigateToLink: HandlerOf<string>;
  openCreateAccountForm: Handler;
  renderImportButton: Renderer;
  onFetchTableDataAsync: (
    cancelToken: CancelToken,
    sortBy?: string,
    sortOrder?: VirtualTableSortableColumnDirectionsEnum,
  ) => Promise<void>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 36px;
  position: relative;
`;

const StyledImg = styled.img`
  width: 92px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const StyledActionIcon = styled(Icon)`
  margin-right: 6px;
`;

/*
 * Component.
 */

export const AccountsTable: FC<AccountsTableProps> = (props) => {
  const {
    panelId,
    selectedAccountId,
    accountIds,
    dynamicColumns,
    paginationData,
    tableStatus,
    onFetchTableDataAsync,
    onReset,
    navigateToLink,
    openCreateAccountForm,
    renderImportButton,
  } = props;

  const tableRef = useRef<VirtualTableHandle>();

  const sortedColumns = useMemo(
    () => contactsManagerAccountsSortColumns([accountsTableColumns[1], ...dynamicColumns]),
    [dynamicColumns],
  );
  const tableColumns = [accountsTableColumns[0], ...sortedColumns];
  const [columnSortOptions, setColumnSortOptions] = useState(initialColumnSort);

  const reloadTable = () => {
    if (tableRef.current) {
      tableRef.current.forceReload();
    }
  };

  const source = useAsyncTableSource(
    {
      onReset: async () => onReset(),
      fetcher: async ({cancelToken}) =>
        onFetchTableDataAsync(
          cancelToken,
          mapAccountIdToSortableColumnName(columnSortOptions?.id),
          columnSortOptions?.direction,
        ),
      onSortChange: (updatedSortOptions) => {
        setColumnSortOptions(updatedSortOptions);
        onReset();
        reloadTable();
      },
    },
    [],
  );

  // Listen for the FETCH_REQUIRED status set.
  useEffect(() => {
    if (tableStatus === ContactsManagerTableStatusesEnum.FETCH_REQUIRED) {
      reloadTable();
    }
  }, [tableStatus]);

  const renderRow = (accountId: number, options: TableRowRenderOptions) => (
    <AccountsTableRowContainer
      panelId={panelId}
      accountId={accountId}
      isSelected={selectedAccountId === accountId}
      options={options}
      tableColumns={tableColumns}
      onSelectAccount={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        navigateToLink(makeContactsManagerAccountDetailsUrl(accountId));
      }}
    />
  );

  const renderEmpty = () => (
    <ContactsManagerTableEmptyState
      title={<Trans id="accounts-table-empty-state-title">No accounts yet</Trans>}
      image={<StyledImg alt="accounts-table-empty-state-image" src={accountsImageSrc} />}
      description={
        <Trans id="accounts-table-empty-state-description">
          Add accounts to see account information in your inbox and automate workflows using account-based
          rules.
        </Trans>
      }
      learnMoreUrl={accountsLearnMoreLink}
    >
      <Button onClick={openCreateAccountForm}>
        <StyledButtonContentDiv>
          <StyledActionIcon name="newAccountSmall" />
          <Trans id="accounts-table-empty-state-create">Create</Trans>
        </StyledButtonContentDiv>
      </Button>
      {renderImportButton()}
    </ContactsManagerTableEmptyState>
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        ref={tableRef}
        columns={tableColumns}
        sortedColumn={columnSortOptions}
        sortableColumns={accountsTableSortableColumns}
        source={source}
        items={accountIds}
        paginationData={paginationData}
        renderRow={renderRow}
        renderEmpty={renderEmpty}
      />
    </StyledTableContainerDiv>
  );
};
